import { useContent } from "@/content/ContentProvider";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Pill } from "./Reveal";
import { PillButton } from "./Kit";
import ShaderBackground from "./ShaderBackground";
import TalkFirst from "./TalkFirst";
import { AUDIT } from "@/lib/check";

// Share of the scroll range spent filling in the text; the rest holds the finished paragraph before it scrolls away.
const READ_END = 0.85;
const INK = "#050606";

export function ScorecardSlide() {
  return (
    <div
      className="relative h-full overflow-hidden rounded-[28px] bg-black text-white"
      data-testid="scorecard-slide"
    >
      <ShaderBackground dark strength={0.2} lines={12} fadeLeft={0} />
      <div className="relative z-10 mx-auto grid h-full max-w-[1400px] items-center gap-8 p-7 sm:p-12 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <Pill white>The first step · two minutes</Pill>
          <h2 className="nm-title mt-6 max-w-[760px] text-[40px] leading-[1] sm:text-[76px]">
            Find the job worth fixing.
          </h2>
          <p className="mt-6 max-w-[540px] text-[18px] leading-[1.3] text-white/65 sm:text-[20px]">
            Ten questions about one job your team repeats. You get the hours it
            costs each month, where it leaks, and a straight answer on whether
            it is worth an audit.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <PillButton
              to="/scorecard"
              variant="inverse"
              size="lg"
              data-testid="slide-cta"
            >
              Start the check
            </PillButton>
            <TalkFirst location="home_last_slide" />
            <Link
              to="/audit"
              className="w-full text-[13px] text-white/45 transition-colors hover:text-white"
            >
              Then the Audit: {AUDIT.price}, {AUDIT.time.toLowerCase()}. See
              every step.
            </Link>
          </div>
        </div>
        <ol className="hidden space-y-2 lg:col-span-4 lg:col-start-9 lg:block">
          {["The hours", "The leak", "The answer"].map((t, n) => (
            <li
              key={t}
              className="flex items-baseline gap-4 rounded-[20px] bg-white/[0.05] px-6 py-5 ring-1 ring-white/10"
            >
              <span className="text-[11px] tabular-nums text-white/35">
                0{n + 1}
              </span>
              <span className="nm-title text-[26px] leading-none">{t}</span>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

// Pinned section: the text fills in letter by letter as you scroll.
export default function Manifesto() {
  const { text: cmsText } = useContent();
  const copy = cmsText("Home.manifesto");
  const words = useMemo(() => copy.split(" "), [copy]);
  const wrap = useRef(null);
  const chars = useRef([]);
  const shown = useRef(0);
  const [reduce] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );

  useEffect(() => {
    if (reduce) return undefined;
    let raf = 0;
    shown.current = 0;
    const calc = () => {
      raf = 0;
      const el = wrap.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const lead = window.innerHeight * 0.55;
      const total = r.height - window.innerHeight + lead;
      const p = Math.min(1, Math.max(0, (lead - r.top) / total));
      const count = chars.current.length;
      const cut = Math.round(Math.min(1, p / READ_END) * count);
      const from = Math.min(cut, shown.current);
      const to = Math.max(cut, shown.current);
      for (let i = from; i < to; i++) {
        const c = chars.current[i];
        if (c) c.style.color = i < cut ? INK : "";
      }
      shown.current = cut;
    };
    const on = () => {
      if (!raf) raf = requestAnimationFrame(calc);
    };
    calc();
    window.addEventListener("scroll", on, { passive: true });
    window.addEventListener("resize", on);
    return () => {
      window.removeEventListener("scroll", on);
      window.removeEventListener("resize", on);
      cancelAnimationFrame(raf);
    };
  }, [reduce, copy]);

  let index = 0;
  const text = (
    <p
      aria-label={copy}
      className={`nm-display mx-auto max-w-[1400px] text-[30px] leading-[1.14] tracking-[-0.015em] sm:text-[48px] lg:text-[64px] ${reduce ? "text-[#050606]" : "text-black/25"}`}
    >
      {words.map((word, w) => (
        <React.Fragment key={w}>
          <span aria-hidden="true" className="inline-block whitespace-nowrap">
            {[...word].map((ch) => {
              const i = index++;
              return (
                <span
                  key={i}
                  ref={(el) => {
                    chars.current[i] = el;
                  }}
                  style={{ transition: "color .18s linear" }}
                >
                  {ch}
                </span>
              );
            })}
          </span>{" "}
        </React.Fragment>
      ))}
    </p>
  );
  chars.current.length = index;

  if (reduce)
    return (
      <section id="manifesto" className="bg-[#f7f7f7]" data-testid="manifesto">
        <div className="px-5 py-24">{text}</div>
      </section>
    );

  return (
    <section
      id="manifesto"
      ref={wrap}
      className="relative h-[220vh] bg-[#f7f7f7]"
      data-testid="manifesto"
    >
      <div className="sticky top-0 flex h-[100svh] items-center overflow-hidden px-5 pt-16">
        {text}
      </div>
    </section>
  );
}
